import { Observable, Observer, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AccessToken, IDToken, OktaAuth }  from '@okta/okta-auth-js';
import { environment } from '../../environments/environment';
import { UserData } from './auth.model';
import { HttpClient } from '@angular/common/http';
import { Logger } from '../shared/logger/logger.component';
import { Ga4EnterpriseLibraryService } from 'vizient-ga4-enterprise'

@Injectable()
export class CustomOktaAuthService {

  CLIENT_ID = environment.CLIENT_ID;
  ISSUER = environment.ISSUER;
  LOGIN_REDIRECT_URI = `${environment.appURL}implicit/callback`;
  LOGOUT_REDIRECT_URI = environment.appURL;

  oktaAuth = new OktaAuth({
    clientId: this.CLIENT_ID,
    issuer: this.ISSUER,
    redirectUri: this.LOGIN_REDIRECT_URI,
    pkce: false,
    tokenManager: {
      storage: 'sessionStorage',
      autoRenew: true
    }
  });

  private loginCompleted = new Subject<UserData>();
  private static timeOfLastRefresh = 0;

  authSession = false;

  constructor(
    private router: Router,
    private http: HttpClient,
    private logger: Logger
  ) { }

  userAuthenticatedListener() {
    return this.loginCompleted.asObservable();
  }

  async isAuthenticated() {
    // Checks if there is a current accessToken in the TokenManger.
    this.checkExpired();
    // return !!(this.oktaAuth.tokenManager.get('accessToken'));
    let token:AccessToken = await this.getAccessToken() as AccessToken;//sessionStorage.getItem("access_token");
    return token && token.accessToken;
  }

  async getAccessToken() {
    // Checks if there is a current accessToken in the TokenManger.
    return await this.oktaAuth.tokenManager.get('accessToken') as AccessToken;
  }

  async getIdToken() {
    // Checks if there is a current accessToken in the TokenManger.
    return await this.oktaAuth.tokenManager.get('idToken') as IDToken;
  }

  async isIAMAuthorized() {
    // Checks if there is a current accessToken in the TokenManger.
    return !!(await sessionStorage.getItem('members'));
  }

  login() {
    const originalUrl = environment.appURL;
    // Save current URL before redirect
    sessionStorage.setItem('okta-app-url', originalUrl || this.router.url);

    // Launches the login r7direct.
    this.oktaAuth.token.getWithRedirect({
      scopes: ['openid', 'email', 'profile']
    });
  }

  async handleAuthentication() {
    try {
      const tokens = await this.oktaAuth.token.parseFromUrl();
      if (!tokens || !tokens.tokens) {
        this.router.navigate(['/logout']);
      }
      const idToken = tokens.tokens.idToken;
      const accessToken = tokens.tokens.accessToken;
      if (idToken) {
        this.oktaAuth.tokenManager.add('idToken', idToken);
          // console.log("idToken: ", idToken);
      }
      if (accessToken) {
        this.oktaAuth.tokenManager.add('accessToken', accessToken);
        // console.log("accessToken: ", accessToken);
      }
      return await this.isAuthenticated();
    } catch (error) {
      this.logger.log('Authentication Error');
      return false;
    }

  }

  async logout() {

    const oldDate = (new Date('Jan 1 2000')).toString();
    const cookies = document.cookie.split(';');

    cookies.forEach(c => {
      const parts = c.split('=');
      const name = parts[0];
      document.cookie = name + '=;expires=' + oldDate + '; path=/;';
    });

    if (sessionStorage.getItem('members')) {
      sessionStorage.removeItem('members');
    }

    if (sessionStorage.getItem('okta-app-url')) {
      sessionStorage.removeItem('okta-app-url');
    }

    if (sessionStorage.getItem('reportState')) {
      sessionStorage.removeItem('reportState');
    }

    if (sessionStorage.getItem('defaultReport')) {
      sessionStorage.removeItem('defaultReport');
    }
    const apiUrl = environment.baseAPIURL + 'Logoff';

    this.http.post(apiUrl, null).subscribe(data => {
      console.log(data);
    })

    await this.oktaAuth.signOut({
      postLogoutRedirectUri: this.LOGOUT_REDIRECT_URI
    });

  }

  createUserData(idToken: any = null, accessToken: any = null) {
    let userdata: UserData = null;
    if (idToken && accessToken) {
      userdata = {
        email: idToken.claims.email,
        username: idToken.claims.name,
        user_guid: idToken.claims.user_guid,
        idToken: idToken.value,
        accessToken: accessToken.value,
        expiresAt: idToken.expiresAt
      };
      //store user_guid in session storage
      if(!sessionStorage.getItem('user_guid') && idToken.claims.user_guid) {
        sessionStorage.setItem('user_guid', idToken.claims.user_guid);
      }
      if(!sessionStorage.getItem('username') && idToken.claims.name) {
        sessionStorage.setItem('username', idToken.claims.name);
      }
      this.callGoogleAnalytics(idToken.claims.user_guid);
    }
    this.authSession = true;
    this.loginCompleted.next(userdata);
  }

  getIAMRoles(): Observable<any> {
    const apiUrl = environment.baseAPIURL + `GetIAMRoles`;
    return this.http.get(apiUrl);
  }

  async handAuthResponse(members) {
    let accessToken = null;
    let idToken = null;
    // console.log("IAMroles: ", members);
    if (!members) {
      this.createUserData();
      this.router.navigate(['/unauthorized']);
    } else {
      accessToken = await this.getAccessToken();
      idToken = await this.getIdToken();
      sessionStorage.setItem('members', members);

      this.createUserData(idToken, accessToken);
      this.router.navigate(['/']);
    }
  }

  async autoAuthUser() {
    let accessToken = null;
    let idToken = null;

    const authData = await this.isAuthenticated();
    if (!authData || this.authSession) {
      return;
    }

    accessToken = await this.getAccessToken();
    idToken = await this.getIdToken();
    this.createUserData(idToken, accessToken);
  }

  checkExpired() {
    this.oktaAuth.tokenManager.on('expired', () => {
      this.router.navigate(['/logout']);
    });

    this.oktaAuth.tokenManager.on('error', () => {
      this.router.navigate(['/logout']);
    });
  }

  refreshToken(source){
    const currentTime = new Date();
        const timePassed = currentTime.valueOf() - CustomOktaAuthService.timeOfLastRefresh;
        //this.logger.log('hitting refresh method: ' + source);

        if (timePassed >= 60000) {
          //this.logger.log('refreshing token')
            CustomOktaAuthService.timeOfLastRefresh = currentTime.valueOf();

            this.oktaAuth.tokenManager.renew('idToken');
            this.oktaAuth.tokenManager.renew('accessToken');
        }
  }

  callGoogleAnalytics(userGuid) {
    if(!userGuid) 
      return;
    const googleAnalytics4 = new Ga4EnterpriseLibraryService(userGuid, environment.googleTagManagerId);
  }
  
  // async handleAuthenticationWithPromise() {
  //   this.oktaAuth.token.parseFromUrl().then(res => {
  //     const tokens = res.tokens;
  //     console.log(tokens);
  //     if (!tokens) {
  //       this.router.navigate(['/logout']);
  //     }
  //     const idToken = tokens.idToken;
  //     const accessToken = tokens.accessToken;
  //     if (idToken) {
  //       this.oktaAuth.tokenManager.add('idToken', idToken);
  //       // console.log("idToken: ", idToken);
  //     }
  //     if (accessToken) {
  //       this.oktaAuth.tokenManager.add('accessToken', accessToken);
  //       //console.log("accessToken: ", accessToken);
  //     }

  //     if (this.isAuthenticated()) {

  //       this.getIAMRoles().subscribe(members => {
  //         console.log("IAMroles: ", members);

  //         if (!members) {
  //           this.createUserData();
  //           this.router.navigate(['/unauthorized']);
  //         }
  //         else {
  //           sessionStorage.setItem('members', members);
  //           this.createUserData(idToken, accessToken);
  //           this.router.navigate(['/terminationreport']);
  //         }
  //       });
  //     }
  //   }).catch(err => {
  //     console.log(err);
  //     this.logout();
  //   });

  // }
}
